'use client';

import React, { useEffect } from 'react';
import { ReactIntegration } from '@grafana/faro-react';
import { faroService } from '@/services/Metrics/FaroService';

interface FaroReactIntegrationProps {
  children: React.ReactNode;
}

/**
 * FaroReactIntegration Component
 * 
 * This component sets up React-specific Faro integrations including:
 * - React Error Boundaries for automatic error capture
 * - Component lifecycle tracking
 * - React-specific performance monitoring
 */
export const FaroReactIntegration: React.FC<FaroReactIntegrationProps> = ({ children }) => {
  useEffect(() => {
    // Prevent infinite recursion with retry limits and timeout cleanup
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds max (50 * 100ms)
    let timeoutId: NodeJS.Timeout | null = null;
    let isCleanedUp = false;
    let isIntegrationReady = false; // Use local flag instead of state to avoid dependency issues

    // Wait for Faro to be initialized before setting up React integration
    const checkFaroAndSetupIntegration = () => {
      // Prevent execution if component unmounted or already cleaned up
      if (isCleanedUp) {
        return;
      }

      const faro = faroService.getFaro();
      
      if (!faro) {
        retryCount++;
        
        if (retryCount >= maxRetries) {
          console.warn('[FaroReactIntegration] Max retries reached, Faro initialization failed');
          isIntegrationReady = false;
          return;
        }
        
        console.log(`[FaroReactIntegration] Faro not yet initialized, waiting... (${retryCount}/${maxRetries})`);
        
        // Schedule next retry with cleanup safety
        timeoutId = setTimeout(() => {
          if (!isCleanedUp) {
            checkFaroAndSetupIntegration();
          }
        }, 100);
        return;
      }

      if (isIntegrationReady) {
        // Already set up
        return;
      }

      try {
        // Add React Integration to existing Faro instance
        const reactIntegration = new ReactIntegration({
          // Note: For Next.js, we don't use React Router, so we skip router integration
          // Navigation tracking is handled by our custom Next.js navigation tracking
        });

        // Register the React integration with Faro
        faro.instrumentations.add(reactIntegration);
        
        console.log('[FaroReactIntegration] React integration added to Faro successfully');
        isIntegrationReady = true;
      } catch (error) {
        console.error('[FaroReactIntegration] Failed to add React integration:', error);
        isIntegrationReady = false;
      }
    };

    // Start checking for Faro initialization
    checkFaroAndSetupIntegration();

    // Cleanup function to prevent memory leaks and infinite recursion
    return () => {
      isCleanedUp = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, []); // No dependencies needed - integration is one-time setup

  return <>{children}</>;
};

/**
 * FaroErrorBoundary Component
 * 
 * React Error Boundary that automatically reports errors to Faro
 */
interface FaroErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class FaroErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error }> },
  FaroErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error }> }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): FaroErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Report error to Faro (with fallback if not initialized)
    const faro = faroService.getFaro();
    if (faro) {
      // Faro will automatically capture this error, but we can add extra context
      faroService.trackEvent('react_error_boundary', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        category: 'react_error',
      });
      console.log('[FaroErrorBoundary] Error reported to Faro');
    } else {
      console.warn('[FaroErrorBoundary] Faro not available, error not reported to Faro');
    }

    console.error('[FaroErrorBoundary] React error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} />;
      }
      
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>An error occurred in this component. The error has been reported.</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * withFaroTracking HOC
 * 
 * Higher-order component that adds Faro tracking to any React component
 */
export function withFaroTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
) {
  const displayName = componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const FaroTrackedComponent: React.FC<P> = (props) => {
    useEffect(() => {
      // Track component mount (with fallback if Faro not ready)
      if (faroService.isReady()) {
        faroService.trackEvent('component_mount', {
          component: displayName,
          category: 'react_lifecycle',
        });
      }

      return () => {
        // Track component unmount (with fallback if Faro not ready)
        if (faroService.isReady()) {
          faroService.trackEvent('component_unmount', {
            component: displayName,
            category: 'react_lifecycle',
          });
        }
      };
    }, []);

    return <WrappedComponent {...props} />;
  };

  FaroTrackedComponent.displayName = `withFaroTracking(${displayName})`;
  return FaroTrackedComponent;
}

/**
 * useFaroTracking Hook
 * 
 * React hook for tracking events and measurements within components
 */
export function useFaroTracking() {
  const trackEvent = (name: string, attributes?: Record<string, any>) => {
    if (faroService.isReady()) {
      faroService.trackEvent(name, attributes);
    } else {
      console.warn(`[useFaroTracking] Faro not ready, skipping event: ${name}`);
    }
  };

  const trackMeasurement = (name: string, value: number, attributes?: Record<string, any>) => {
    if (faroService.isReady()) {
      faroService.trackMeasurement(name, value, attributes);
    } else {
      console.warn(`[useFaroTracking] Faro not ready, skipping measurement: ${name}`);
    }
  };

  const trackGameEvent = (eventType: string, gameData: Record<string, any>) => {
    if (faroService.isReady()) {
      faroService.trackGameEvent(eventType, gameData);
    } else {
      console.warn(`[useFaroTracking] Faro not ready, skipping game event: ${eventType}`);
    }
  };

  const trackSTTEvent = (eventType: string, performanceData: Record<string, any>) => {
    if (faroService.isReady()) {
      faroService.trackSTTEvent(eventType, performanceData);
    } else {
      console.warn(`[useFaroTracking] Faro not ready, skipping STT event: ${eventType}`);
    }
  };

  return {
    trackEvent,
    trackMeasurement,
    trackGameEvent,
    trackSTTEvent,
    isReady: faroService.isReady(),
  };
} 